import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { BuildingOfficeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { trpc } from '@utils/trpc';
import switchBusiness from '@utils/switchBusiness';

interface BusinessSelectorModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const BusinessSelectorModal = ({ isOpen, onClose }: BusinessSelectorModalProps) => {
	const [isSwitching, setIsSwitching] = useState(false);

	// Fetch available businesses for current user
	const { data: businesses, isLoading } = trpc.business.listAvailableBusinesses.useQuery(undefined, {
		enabled: isOpen,
	});

	const handleBusinessSwitch = async (businessId: number) => {
		setIsSwitching(true);

		try {
			// Call the API endpoint to set the cookie
			const response = await fetch('/api/business/switch', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ businessId }),
			});

			if (!response.ok) {
				throw new Error('Failed to switch business');
			}

			switchBusiness();

			// Redirect to the root of the app to apply the new business context
			window.location.href = '/';
		} catch {
			setIsSwitching(false);
		}
	};

	return (
		<Transition.Root show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={isSwitching ? () => {} : onClose}>
				{/* Backdrop */}
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
				</Transition.Child>

				{/* Modal */}
				<div className="fixed inset-0 z-10 overflow-y-auto">
					<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
								<div>
									<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
										<BuildingOfficeIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
									</div>
									<div className="mt-3 text-center sm:mt-5">
										<Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
											Switch Business
										</Dialog.Title>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Select a business to switch to. The page will reload with the new business context.
											</p>
										</div>
									</div>
								</div>

								{/* Business List */}
								<div className="mt-5">
									{isLoading ? (
										<div className="flex justify-center py-8">
											<div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-blue-600"></div>
										</div>
									) : businesses && businesses.length > 0 ? (
										<ul role="list" className="divide-y divide-gray-200 rounded-md border border-gray-200">
											{businesses.map((business) => (
												<li
													key={business.businessId}
													className={`flex items-center justify-between py-3 px-4 hover:bg-gray-50 ${
														business.isCurrentBusiness ? 'bg-blue-50' : ''
													} ${isSwitching ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
													onClick={() => {
														if (!isSwitching && !business.isCurrentBusiness) {
															void handleBusinessSwitch(business.businessId);
														}
													}}
												>
													<div className="flex items-center">
														<BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
														<div>
															<p className="text-sm font-medium text-gray-900">{business.businessName}</p>
														</div>
													</div>
													{business.isCurrentBusiness && (
														<div className="flex items-center">
															<CheckCircleIcon className="h-5 w-5 text-blue-600" />
															<span className="ml-2 text-sm font-medium text-blue-600">Current</span>
														</div>
													)}
												</li>
											))}
										</ul>
									) : (
										<div className="text-center py-8">
											<p className="text-sm text-gray-500">No businesses available</p>
										</div>
									)}
								</div>

								{/* Actions */}
								<div className="mt-5 sm:mt-6">
									<button
										type="button"
										className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
										onClick={onClose}
										disabled={isSwitching}
									>
										{isSwitching ? 'Switching...' : 'Cancel'}
									</button>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition.Root>
	);
};
